import { PlatformLocation } from '@angular/common';
import type { AfterViewInit, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

import { FeeDisclaimerService } from 'app/shared/services/fee-disclaimer.service';
import { PassDataService } from '../../mega-exchange/services/pass-data.service';
import { StringConstants } from '../../rcr-exchange/constants/string-constants';
import { FmLoadingPageService } from '../../shared/services/fm-loading-page.service';
import { MegaTradeNavigationService } from '../services/mega-trade-navigation.service';
import { MegasService } from '../services/megas.service';

@Component({
  standalone: false,
  selector: 'invd-bulk-upload-review-and-commit',
  templateUrl: './bulk-upload-review-and-commit.component.html',
  styleUrls: ['./bulk-upload-review-and-commit.component.scss'],
})
export class BulkUploadReviewAndCommitComponent implements OnInit, AfterViewInit {
  public headerText: string;
  public headerSubText: string;
  public showCancelPopup = false;
  public isAllowToNavigateAway = false;
  public multiMegaCalculateFeeResponse: any;
  public validatedMegaList: any[];
  public megaPricingUpdateList: any[];
  public showEarlyCloseLegend = false;
  public showBanner = false;
  public messageBody = '';
  public showCommitError = false;
  public commitErrorMessage = '';
  public showEarlyCloseLegendPricingSummary = false;
  public isCommitButtonClicked = false;
  public disclaimerMessage = '';

  private router = inject(Router);
  private passDataService = inject(PassDataService);
  private location = inject(PlatformLocation);
  private fmLoadingService = inject(FmLoadingPageService);
  private megaNavService = inject(MegaTradeNavigationService);
  private feeDisclaimerService = inject(FeeDisclaimerService);
  private megaService = inject(MegasService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this.headerText = StringConstants.MEGA_BULK_UPLOAD_ENTER_DETAILS_SCREEN_HEADER;
    this.headerSubText = StringConstants.MEGA_BULK_REVIEW_AND_COMMIT_SCREEN_SUB_HEADER;
    this.fmLoadingService.isLoaderRequesting.set(false);
    this.multiMegaCalculateFeeResponse = this && this.passDataService.getMultiMegaResponse();
    this.validatedMegaList =
      this.multiMegaCalculateFeeResponse.megaPricingInfo &&
      this.multiMegaCalculateFeeResponse.megaPricingInfo[0].newMegaList;
    this.megaPricingUpdateList =
      this.multiMegaCalculateFeeResponse.megaPricingInfo &&
      this.multiMegaCalculateFeeResponse.megaPricingInfo[0].existingMegaList;
    if (
      this.multiMegaCalculateFeeResponse &&
      this.multiMegaCalculateFeeResponse.infoMessage &&
      this.multiMegaCalculateFeeResponse.infoMessage !== null
    ) {
      this.displayWarningMessage(this.multiMegaCalculateFeeResponse.infoMessage);
    }
    this.lookupForEarlyCloseFlag();
    this.checkDisclaimerStatus();
  }

  private checkDisclaimerStatus(): void {
    const allMegas = [...(this.validatedMegaList || []), ...(this.megaPricingUpdateList || [])];
    const hasDisclaimer = allMegas.some((mega) =>
      this.feeDisclaimerService.needsDisclaimer(mega?.prefix),
    );
    if (hasDisclaimer) {
      this.disclaimerMessage = StringConstants.SUPERS_PREFIX_FEE_DISCLAIMER;
    }
  }

  private displayWarningMessage(msg): void {
    this.showBanner = true;
    this.messageBody = msg;
  }

  public closeMessageBanner(): void {
    this.showBanner = false;
  }

  public closeCommitErrorBanner(): void {
    this.showCommitError = false;
  }

  ngAfterViewInit() {
    this.navigationController();
  }

  public lookupForEarlyCloseFlag() {
    const isEarlyCloseDateNewMegas =
      this.validatedMegaList && this.validatedMegaList.some((val) => val.earlyCloseFlag);
    const isEarlyCloseDateExistingMegas =
      this.megaPricingUpdateList && this.megaPricingUpdateList.some((val) => val.earlyCloseFlag);
    if (isEarlyCloseDateNewMegas) {
      this.showEarlyCloseLegend = true;
    }
    if (isEarlyCloseDateExistingMegas) {
      this.showEarlyCloseLegendPricingSummary = true;
    }
  }

  public navigatePageTo(pageURL: string) {
    this.router.navigate([pageURL]);
    this.isAllowToNavigateAway = true;
  }

  canDeactivate(): Promise<boolean> | boolean {
    return this.isAllowToNavigateAway;
  }

  public goBack(): void {
    let megaTypeDesc = '';
    this.passDataService.setGoBackEditFlag('true');
    megaTypeDesc =
      this.multiMegaCalculateFeeResponse &&
      this.multiMegaCalculateFeeResponse.megaType === 'SFFIXMEGA'
        ? StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX_DESC
        : this.multiMegaCalculateFeeResponse &&
            this.multiMegaCalculateFeeResponse.megaType === 'SFARMMEGA'
          ? StringConstants.MEGA_TYPE_SINGLE_FAMILY_ARM_DESC
          : this.multiMegaCalculateFeeResponse &&
              this.multiMegaCalculateFeeResponse.megaType === 'MFFIXMEGA'
            ? StringConstants.MEGA_TYPE_MULTI_FAMILY_FIX_DESC
            : this.multiMegaCalculateFeeResponse &&
                this.multiMegaCalculateFeeResponse.megaType === 'MFARMMEGA'
              ? StringConstants.MEGA_TYPE_MULTI_FAMILY_ARM_DESC
              : StringConstants.MEGA_TYPE_SINGLE_FAMILY_FIX_DESC;
    this.passDataService.setMegaType(megaTypeDesc);
    this.passDataService.setMultiMegaResponse(this.multiMegaCalculateFeeResponse);
    this.location.back();
  }

  private navigationController() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.url.indexOf('enter-mega-details') > 0) {
          this.isAllowToNavigateAway = true;
        } else if (event.url.indexOf('error') > 0) {
          this.isAllowToNavigateAway = true;
        } else {
          this.isAllowToNavigateAway = true;
        }
      }
      if (!(event instanceof NavigationEnd)) {
        return;
      }
      window.scrollTo(0, 0);
    });
  }

  public cancelMultiMegaCommit(): void {
    this.router.navigate(['/bulk-upload/enter-mega-details']);
  }

  public commitMultipleMegas(): void {
    this.isCommitButtonClicked = true;
    this.fmLoadingService.isLoaderRequesting.set(true);
    const request: any = new Object();

    const megaDetails: any = [];

    this.passDataService.getMultiMegaResponse().megaPricingInfo[0].newMegaList.forEach(function (
      item,
    ) {
      const megaAttributes: any = {};
      megaAttributes.rowNumber = item.rowNumber;
      megaAttributes.megaType =
        this.passDataService.getMultiMegaResponse() &&
        this.passDataService.getMultiMegaResponse().megaType;
      megaAttributes.mfHighCoupon = item.mfHighCoupon;
      megaAttributes.mfLowCoupon = item.mfLowCoupon;
      megaAttributes.feeTicks = item.newFeeTicks;
      megaAttributes.feeDollars = item.newFeeDollars;
      megaAttributes.prefixCode = item.prefix;
      megaAttributes.sfFixedCoupon = item.sfFixedCoupon;
      megaAttributes.sfARMSubType = item.sfARMSubType;
      megaAttributes.tradeAmount = item.tradeAmount;
      megaAttributes.tradeSettlementDate = this.setDateFormat(item.tradeSettlementDate).toString();
      megaDetails.push(megaAttributes);
    }, this);

    request.packageRevisionNumber =
      this.passDataService.getMultiMegaResponse() &&
      this.passDataService.getMultiMegaResponse().packageRevisionNumber;
    request.megasDetails = megaDetails;

    this.megaService.multipleMegaCommit(request).subscribe({
      next: (data) => this.handleMultipleMegaCommitSuccess(data),
      error: (error) => this.handleMultipleMegaCommitServiceFail(error),
    });
  }

  public handleMultipleMegaCommitSuccess(commitResponse) {
    this.fmLoadingService.isLoaderRequesting.set(false);
    this.passDataService.setMultiMegaCommitResponse(commitResponse);
    this.megaNavService.canNavigate = true;
    this.cdr.markForCheck();
    this.router.navigate(['/bulk-upload/enter-mega-details/confirmation']);
  }

  public handleMultipleMegaCommitServiceFail(error: any): void {
    if (error.status === 400) {
      this.showCommitError = true;
      this.commitErrorMessage = error && error.error && error.error.messages[0].errorMessage;
    }

    this.fmLoadingService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public setDateFormat(val): string {
    let dateForm: any;
    if (val !== null && /^\d{4}\-\d{1,2}\-\d{1,2}$/.test(val)) {
      dateForm = val && val.split('-');
      val = [dateForm[1], dateForm[2], dateForm[0]].join('/');
      return val;
    } else {
      return val;
    }
  }

  public needsDisclaimer(prefixCode: string): boolean {
    if (this.feeDisclaimerService.needsDisclaimer(prefixCode)) {
      this.disclaimerMessage = StringConstants.SUPERS_PREFIX_FEE_DISCLAIMER;
      return true;
    }
    return false;
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
